<template>
  <div id="seed-generate">
    <div v-if="error" class="alert alert-danger" role="alert">
      <button type="button" class="close" aria-label="Close">
        <img class="icon" src="/i/svg/x.svg" alt="clear" @click="error = false" />
      </button>
      <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
      <span class="sr-only">{{ $t('error.title') }}:</span>
      <span class="message">{{ this.error }}</span>
    </div>
    <rom-loader v-if="!romLoaded" @update="updateRom" @error="onError"></rom-loader>
    <div v-if="romLoaded && !gameLoaded && !generating" class="card border-success">
      <div class="card-header bg-success card-heading-btn">
        <h3 class="card-title text-white float-left">{{ $t('randomizer.title') }}</h3>
        <div class="btn-toolbar float-right" v-if="gameGenerated">
          <a
            class="btn btn-light text-dark border-secondary"
            role="button"
            @click="gameLoaded = true"
          >
            {{ $t('randomizer.generate.forward') }}
            <img class="icon" src="/i/svg/arrow-right.svg" alt />
          </a>
        </div>
      </div>
      <div class="card-body">
        <div class="card border-info my-1">
          <div class="card-body">
            <div class="row">
              <div class="col my-1">
                <Select :value="preset" @input="setPreset" :options="optionsPreset">
                  <template v-slot:default>{{ $t('randomizer.preset.title') }}</template>
                  <template v-slot:appends v-if="preset.value !== 'custom'">
                    <button
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="setPreset('custom')"
                    >{{ $t('randomizer.preset.customize') }}</button>
                  </template>
                </Select>
              </div>
            </div>
          </div>
          <h5 class="card-title p-2 border-bottom">{{ $t('randomizer.placement.title') }}</h5>
          <div class="card-body">
            <div class="row" v-if="!editable">
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.glitches_required.title') }}: {{ $t(glitchesRequired.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.item_placement.title') }}: {{ $t(itemPlacement.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.dungeon_items.title') }}: {{ $t(dungeonItems.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.boss_items.title') }}: {{ $t(bossItems.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.accessibility.title') }}: {{ $t(accessibility.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.drop_shuffle.title') }}: {{ $t(dropShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.bonk_shuffle.title') }}: {{ $t(bonkShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.pottery_shuffle.title') }}: {{ $t(potteryShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.shopsanity.title') }}: {{ $t(shopsanity.name) }}</div>
            </div>
            <div class="row" v-if="editable">
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="glitchesRequired"
                  @input="setGlitchesRequired"
                  :options="optionsGlitchesRequired"
                >{{ $t('randomizer.glitches_required.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="itemPlacement"
                  @input="setItemPlacement"
                  :options="optionsItemPlacement"
                >{{ $t('randomizer.item_placement.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="dungeonItems"
                  @input="setDungeonItems"
                  :options="optionsDungeonItems"
                >{{ $t('randomizer.dungeon_items.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="bossItems"
                  @input="setBossItems"
                  :options="optionsBossItems"
                >{{ $t('randomizer.boss_items.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="accessibility"
                  @input="setAccessibility"
                  :options="optionsAccessibility"
                >{{ $t('randomizer.accessibility.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="dropShuffle"
                  @input="setDropShuffle"
                  :options="optionsDropShuffle"
                >{{ $t('randomizer.drop_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="bonkShuffle"
                  @input="setBonkShuffle"
                  :options="optionsBonkShuffle"
                >{{ $t('randomizer.bonk_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="potteryShuffle"
                  @input="setPotteryShuffle"
                  :options="optionsPotteryShuffle"
                >{{ $t('randomizer.pottery_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="shopsanity"
                  @input="setShopsanity"
                  :options="optionsShopsanity"
                >{{ $t('randomizer.shopsanity.title') }}</Select>
              </div>
            </div>
            <div
              v-if="glitchesRequired.value !== 'none'"
              class="logic-warning text-danger"
              v-html="$t('randomizer.glitches_required.glitch_warning')"
            />
          </div>
          <h5 class="card-title p-2 border-bottom">{{ $t('randomizer.goal.title') }}</h5>
          <div class="card-body">
            <div class="row" v-if="!editable">
              <div
                class="col-xl-6 col-lg-6 my-1"
              >{{ $t('randomizer.goal.title') }}: {{ $t(goal.name) }}</div>
              <div
                class="col-xl-6 col-lg-6 my-1"
              >{{ $t('randomizer.tower_open.title') }}: {{ $t(towerOpen.name) }}</div>
              <div
                class="col-xl-6 col-lg-6 my-1" v-if="!['triforce-hunt', 'pedestal', 'ambroz1a', 'dungeons'].includes(goal.value)"
              >{{ $t('randomizer.ganon_open.title') }}: {{ $t(ganonOpen.name) }}</div>
              <div
                class="col-xl-6 col-lg-6 my-1" v-if="!['triforce-hunt', 'pedestal'].includes(goal.value)"
              >{{ $t('randomizer.ganon_item.title') }}: {{ $t(ganonItem.name) }}</div>
            </div>
            <div class="row" v-if="editable">
              <div class="col-xl-6 col-lg-6 my-1">
                <Select
                  :value="goal"
                  @input="setGoal"
                  :options="optionsGoal"
                >{{ $t('randomizer.goal.title') }}</Select>
              </div>
              <div class="col-xl-6 col-lg-6 my-1">
                <Select
                  :value="towerOpen"
                  @input="setTowerOpen"
                  :options="optionsTowerOpen"
                >{{ $t('randomizer.tower_open.title') }}</Select>
              </div>
              <div class="col-xl-6 col-lg-6 my-1" v-if="!['triforce-hunt', 'pedestal', 'ambroz1a', 'dungeons'].includes(goal.value)">
                <Select
                  :value="ganonOpen"
                  @input="setGanonOpen"
                  :options="optionsGanonOpen"
                >{{ $t('randomizer.ganon_open.title') }}</Select>
              </div>
              <div class="col-xl-6 col-lg-6 my-1" v-if="!['triforce-hunt', 'pedestal'].includes(goal.value)">
                <Select
                  :value="ganonItem"
                  @input="setGanonItem"
                  :options="optionsGanonItem"
                >{{ $t('randomizer.ganon_item.title') }}</Select>
              </div>
            </div>
          </div>
          <h5 class="card-title p-2 border-bottom">{{ $t('randomizer.gameplay.title') }}</h5>
          <div class="card-body" v-if="!editable">
            <div class="row">
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.world_state.title') }}: {{ $t(worldState.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.entrance_shuffle.title') }}: {{ $t(entranceShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.hints.title') }}: {{ $t(hints.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.boss_shuffle.title') }}: {{ $t(bossShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.enemy_shuffle.title') }}: {{ $t(enemyShuffle.name) }}</div>
            </div>
          </div>
          <div class="card-body" v-if="editable">
            <div class="row">
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="worldState"
                  @input="setWorldState"
                  :options="optionsWorldState"
                >{{ $t('randomizer.world_state.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="entranceShuffle"
                  @input="setEntranceShuffle"
                  :options="optionsEntranceShuffle"
                >{{ $t('randomizer.entrance_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="hints"
                  @input="setHints"
                  :options="optionsHints"
                >{{ $t('randomizer.hints.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="bossShuffle"
                  @input="setBossShuffle"
                  :options="optionsBossShuffle"
                >{{ $t('randomizer.boss_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="enemyShuffle"
                  @input="setEnemyShuffle"
                  :options="optionsEnemyShuffle"
                >{{ $t('randomizer.enemy_shuffle.title') }}</Select>
              </div>
            </div>
            <div
              v-if="enemyShuffle.value === 'killable'"
              class="logic-warning text-danger"
              v-html="$t('randomizer.enemy_shuffle.killable_warning')"
            />
            <div
              v-if="enemyShuffle.value === 'moldorms'"
              class="logic-warning text-danger"
              v-html="$t('randomizer.enemy_shuffle.moldorm_warning')"
            />
          </div>
          <h5 class="card-title p-2 border-bottom">{{ $t('randomizer.door_shuffle.title') }}</h5>
          <div class="card-body" v-if="!editable">
            <div class="row">
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.door_shuffle.title') }}: {{ $t(doorShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'"
              >{{ $t('randomizer.door_intensity.title') }}: {{ $t(doorIntensity.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'"
              >{{ $t('randomizer.door_type_mode.title') }}: {{ $t(doorTypeMode.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'"
              >{{ $t('randomizer.trap_door_mode.title') }}: {{ $t(trapDoorMode.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'"
              >{{ $t('randomizer.decouple_doors.title') }}: {{ $t(decoupleDoors.name) }}</div>
            </div>
          </div>
          <div class="card-body" v-if="editable">
            <div class="row">
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="doorShuffle"
                  @input="setDoorShuffle"
                  :options="optionsDoorShuffle"
                >{{ $t('randomizer.door_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'">
                <Select
                  :value="doorIntensity"
                  @input="setDoorIntensity"
                  :options="optionsDoorIntensity"
                >{{ $t('randomizer.door_intensity.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'">
                <Select
                  :value="doorTypeMode"
                  @input="setDoorTypeMode"
                  :options="optionsDoorTypeMode"
                >{{ $t('randomizer.door_type_mode.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'">
                <Select
                  :value="trapDoorMode"
                  @input="setTrapDoorMode"
                  :options="optionsTrapDoorMode"
                >{{ $t('randomizer.trap_door_mode.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1" v-if="doorShuffle.value != 'vanilla'">
                <Select
                  :value="decoupleDoors"
                  @input="setDecoupleDoors"
                  :options="optionsDecoupleDoors"
                >{{ $t('randomizer.decouple_doors.title') }}</Select>
              </div>
            </div>
          </div>
          <h5 class="card-title p-2 border-bottom">{{ $t('randomizer.overworld_shuffle.title') }}</h5>
          <div class="card-body" v-if="!editable">
            <div class="row">
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.ow_shuffle.title') }}: {{ $t(overworldShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.ow_crossed.title') }}: {{ $t(overworldCrossed.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1" v-if="overworldShuffle.value != 'vanilla' || overworldCrossed.value == 'limited' || overworldCrossed.value == 'chaos'"
              >{{ $t('randomizer.ow_keep_similar.title') }}: {{ $t(overworldKeepSimilar.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.ow_mixed.title') }}: {{ $t(overworldMixed.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.ow_flute_shuffle.title') }}: {{ $t(overworldFluteShuffle.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.ow_whirlpool_shuffle.title') }}: {{ $t(overworldWhirlpoolShuffle.name) }}</div>
            </div>
          </div>
          <div class="card-body" v-if="editable">
            <div class="row">
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="overworldShuffle"
                  @input="setOverworldShuffle"
                  :options="optionsOverworldShuffle"
                >{{ $t('randomizer.ow_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="overworldCrossed"
                  @input="setOverworldCrossed"
                  :options="optionsOverworldCrossed"
                >{{ $t('randomizer.ow_crossed.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1" v-if="overworldShuffle.value != 'vanilla' || overworldCrossed.value == 'limited' || overworldCrossed.value == 'chaos'">
                <Select
                  :value="overworldKeepSimilar"
                  @input="setOverworldKeepSimilar"
                  :options="optionsOverworldKeepSimilar"
                >{{ $t('randomizer.ow_keep_similar.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="overworldMixed"
                  @input="setOverworldMixed"
                  :options="optionsOverworldMixed"
                >{{ $t('randomizer.ow_mixed.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="overworldFluteShuffle"
                  @input="setOverworldFluteShuffle"
                  :options="optionsOverworldFluteShuffle"
                >{{ $t('randomizer.ow_flute_shuffle.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="overworldWhirlpoolShuffle"
                  @input="setOverworldWhirlpoolShuffle"
                  :options="optionsOverworldWhirlpoolShuffle"
                >{{ $t('randomizer.ow_whirlpool_shuffle.title') }}</Select>
              </div>
            </div>
          </div>
          <h5 class="card-title p-2 border-bottom">{{ $t('randomizer.difficulty.title') }}</h5>
          <div class="card-body">
            <div class="row" v-if="!editable">
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.weapons.title') }}: {{ $t(weapons.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.item_pool.title') }}: {{ $t(itemPool.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.item_functionality.title') }}: {{ $t(itemFunctionality.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.starting_flute.title') }}: {{ $t(startingFlute.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.starting_boots.title') }}: {{ $t(startingBoots.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.enemy_damage.title') }}: {{ $t(enemyDamage.name) }}</div>
              <div
                class="col-xl-4 col-lg-6 my-1"
              >{{ $t('randomizer.enemy_health.title') }}: {{ $t(enemyHealth.name) }}</div>
            </div>
            <div class="row" v-if="editable">
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="weapons"
                  @input="setWeapons"
                  :options="optionsWeapons"
                >{{ $t('randomizer.weapons.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select :value="itemPool" @input="setItemPool" :options="optionsItemPool">
                  {{ $t('randomizer.item_pool.title') }}
                  <sup
                    v-if="itemPool.value === 'crowd_control'"
                  >*</sup>
                </Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="itemFunctionality"
                  @input="setItemFunctionality"
                  :options="optionsItemFunctionality"
                >{{ $t('randomizer.item_functionality.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="startingFlute"
                  @input="setStartingFlute"
                  :options="optionsStartingFlute"
                >{{ $t('randomizer.starting_flute.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="startingBoots"
                  @input="setStartingBoots"
                  :options="optionsStartingBoots"
                >{{ $t('randomizer.starting_boots.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="enemyDamage"
                  @input="setEnemyDamage"
                  :options="optionsEnemyDamage"
                >{{ $t('randomizer.enemy_damage.title') }}</Select>
              </div>
              <div class="col-xl-4 col-lg-6 my-1">
                <Select
                  :value="enemyHealth"
                  @input="setEnemyHealth"
                  :options="optionsEnemyHealth"
                >{{ $t('randomizer.enemy_health.title') }}</Select>
              </div>
            </div>
            <div
              v-if="itemPool.value === 'crowd_control'"
              class="logic-warning text-info"
              v-html="$t('randomizer.item_pool.crowd_control_warning')"
            />
          </div>
        </div>
      </div>
      <div class="card-footer">
        <div
          v-if="doorShuffle.value !== 'vanilla' || dropShuffle.value === 'on'"
          class="logic-warning text-danger"
          v-html="$t('randomizer.doors_warning')"
        />
        <div class="row">
          <div class="col-md">
            <div class="btn-group btn-flex" role="group">
              <button
                class="btn btn-primary w-50 text-center"
                v-tooltip="$t('randomizer.generate.race_warning')"
                :disabled="generating"
                name="generate-tournament-rom"
                @click="applyTournamentSeed"
              >{{ $t('randomizer.generate.race') }}</button>
            </div>
          </div>
          <div class="col-md">
            <div class="btn-group btn-flex" role="group">
              <button
                class="btn btn-info w-50 text-center"
                name="generate-tournament-rom"
                :disabled="generating"
                @click="applyTournamentSpoilerSeed"
              >{{ $t('randomizer.generate.spoiler_race') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-show="generating" class="center">
      <div class="loading" />
      <h1>{{ $t('randomizer.generate.generating') }}</h1>
      <h5>{{ $t('randomizer.generate.slow_warning') }}</h5>
    </div>

    <div
      id="seed-details"
      class="card border-success"
      v-if="gameLoaded && romLoaded && !generating"
    >
      <div class="card-header bg-success text-white card-heading-btn">
        <h3 class="card-title text-white float-left">{{ $t('randomizer.details.title') }}</h3>
        <div class="btn-toolbar float-right">
          <a
            class="btn btn-light text-dark border-secondary"
            role="button"
            @click="gameLoaded = false"
          >
            {{ $t('randomizer.generate.back') }}
            <img class="icon" src="/i/svg/cog.svg" alt />
          </a>
          <a
            class="btn btn-light text-dark border-secondary ml-3"
            role="button"
            v-tooltip="$t('randomizer.generate.regenerate_tooltip')"
            @click="applySeed"
          >
            {{ $t('randomizer.generate.regenerate') }}
            <img class="icon" src="/i/svg/reload.svg" alt />
          </a>
        </div>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md mb-3">
            <vt-rom-info :rom="rom"></vt-rom-info>
          </div>
          <div class="col-md mb-3">
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="btn-group btn-flex" role="group" v-if="this.rom">
                  <button
                    class="btn btn-light border-secondary text-center"
                    @click="saveSpoiler"
                  >{{ $t('randomizer.details.save_spoiler') }}</button>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <div class="btn-group btn-flex" role="group" v-if="this.rom">
                  <button
                    class="btn btn-success text-center"
                    :disabled="disableSaveRomButton"
                    @click="saveRom"
                  >{{ $t('randomizer.details.save_rom') }}</button>
                </div>
              </div>
            </div>
            <div class="row">
              <vt-rom-settings
                class="col-12"
                :rom="rom"
                @disallow-save-rom="disallowSaveRom"
              ></vt-rom-settings>
            </div>
          </div>
        </div>
        <vt-spoiler v-model="show_spoiler" :rom="rom"></vt-spoiler>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus from "../core/event-bus";
import FileSaver from "file-saver";
import RomLoader from "../components/VTRomLoader.vue";
import Select from "../components/Select.vue";
import localforage from "localforage";
import axios from "axios";
import { mapMutations, mapActions, mapState } from "vuex";
import { retry } from "@lifeomic/attempt";

export default {
  components: {
    RomLoader,
    Select
  },
  data() {
    return {
      rom: null,
      error: false,
      generating: false,
      generationId: null,
      romLoaded: false,
      rom_infos: {},
      gameLoaded: false,
      gameGenerated: false,
      show_spoiler: false,
      tournament: false,
      disableSaveRomButton: false,
      spoilers: "off"
    };
  },
  created() {
    this.$store.dispatch("getSprites");
    this.$store.dispatch("randomizer/getItemSettings");
    this.$store.dispatch("romSettings/initialize");

    this.rom_infos = {};
    this.rom = null;
    localforage.getItem("rom").then(function(blob) {
      if (blob == null) {
        EventBus.$emit("noBlob");
        return;
      }
      EventBus.$emit("loadBlob", { target: { files: [new Blob([blob])] } });
    });
  },
  methods: {
    ...mapActions("randomizer", [
      "setPreset",
      "setGlitchesRequired",
      "setItemPlacement",
      "setGoal",
      "setDropShuffle",
      "setBonkShuffle",
      "setPotteryShuffle",
      "setGanonOpen",
      "setDoorShuffle",
      "setOverworldShuffle",
      "setOverworldCrossed",
      "setOverworldMixed",
      "setOverworldFluteShuffle",
      "setOverworldWhirlpoolShuffle",
      "setShopsanity",
      "setEntranceShuffle",
      "setItemPool"
    ]),
    ...mapMutations("randomizer", [
      "setAccessibility",
      "setDungeonItems",
      "setBossItems",
      "setTowerOpen",
      "setGanonItem",
      "setDoorIntensity",
      "setDoorTypeMode",
      "setTrapDoorMode",
      "setDecoupleDoors",
      "setOverworldKeepSimilar",
      "setBossShuffle",
      "setEnemyShuffle",
      "setWeapons",
      "setWorldState",
      "setHints",
      "setItemFunctionality",
      "setStartingFlute",
      "setStartingBoots",
      "setEnemyDamage",
      "setEnemyHealth"
    ]),
    disallowSaveRom(e) {
      this.disableSaveRomButton = Boolean(e);
    },
    applyTournamentSeed() {
      this.tournament = true;
      this.spoilers = "off";
      this.applySeed();
    },
    applyTournamentSpoilerSeed() {
      this.tournament = false;
      this.spoilers = "on";
      this.applySeed();
    },
    applySeed(e, second_attempt) {
      this.error = false;
      this.generating = true;
      return new Promise(
        function(resolve, reject) {
          this.gameLoaded = false;
          axios
            .post(`/api/randomizer`, {
              async: true,
              glitches: this.glitchesRequired.value,
              item_placement: this.itemPlacement.value,
              dungeon_items: this.dungeonItems.value,
              boss_items: this.bossItems.value,
              drop_shuffle: this.dropShuffle.value,
              bonk_shuffle: this.bonkShuffle.value,
              pottery_shuffle: this.potteryShuffle.value,
              accessibility: this.accessibility.value,
              goal: this.goal.value,
              crystals: {
                ganon: this.ganonOpen.value,
                tower: this.towerOpen.value
              },
              ganon_item: this.ganonItem.value,
              mode: this.worldState.value,
              entrances: this.entranceShuffle.value,
              doors: {
                shuffle: this.doorShuffle.value,
                intensity: this.doorIntensity.value,
                type_mode: this.doorTypeMode.value,
                trap_mode: this.trapDoorMode.value,
                decoupled: this.decoupleDoors.value
              },
              overworld: {
                shuffle: this.overworldShuffle.value,
                crossed: this.overworldCrossed.value,
                keep_similar: this.overworldKeepSimilar.value,
                mixed: this.overworldMixed.value,
                flute_shuffle: this.overworldFluteShuffle.value,
                whirlpool_shuffle: this.overworldWhirlpoolShuffle.value
              },
              shopsanity: this.shopsanity.value,
              hints: this.hints.value,
              weapons: this.weapons.value,
              item: {
                pool: this.itemPool.value,
                functionality: this.itemFunctionality.value
              },
              equipment: {
                flute: this.startingFlute.value,
                boots: this.startingBoots.value
              },
              tournament: this.tournament,
              spoilers: this.spoilers,
              lang: document.documentElement.lang,
              enemizer: {
                boss_shuffle: this.bossShuffle.value,
                enemy_shuffle: this.enemyShuffle.value,
                enemy_damage: this.enemyDamage.value,
                enemy_health: this.enemyHealth.value
              }
            })
            .then(generationResponse => {
              if (generationResponse.data.seed_generation_id) {
                this.generationId = generationResponse.data.seed_generation_id;

                retry(
                  this.checkGeneration.bind(this),
                  {
                    delay: 300,
                    maxDelay: 5000,
                    factor: 1.5,
                    maxAttempts: 0,
                    jitter: true,
                    minDelay: 300,
                    handleError (err, context, options) {
                      if (err.message != "not ready yet") {
                        context.abort();
                      }
                    },
                  }
                )
                  .then(response => {
                    let prom;
                    let branch = response.data.branch;
                    if (!this.rom_infos[branch]) {
                      console.error(`No info on branch ${branch}`);
                      // TODO: should probably give a better error message
                      this.error = this.$i18n.t("error.failed_generation");
                      reject(this.error);
                    }
                    let rom = this.rom_infos[branch].rom;
                    let hash = this.rom_infos[branch].hash;
                    if (rom.checkMD5() != hash) {
                      prom = rom.reset().then(() => {
                        if (rom.checkMD5() != hash) {
                          return new Promise((resolve, reject) => {
                            reject(rom);
                          });
                        }
                        return rom.parsePatch(response.data);
                      });
                    } else {
                      prom = rom.parsePatch(response.data);
                    }
                    prom.then(
                      function(rom) {
                        this.rom = rom;
                        if (response.data.current_rom_hash && response.data.current_rom_hash != hash) {
                          // The base ROM has been updated.
                          window.location.assign(`/h/${this.rom.hash}`);
                        }
                        if (this.rom.shuffle || this.rom.spoilers == "mystery" || this.rom.allow_quickswap) {
                          this.rom.allowQuickSwap = true;
                        }
                        this.gameLoaded = true;
                        this.gameGenerated = true;
                        this.generating = false;
                        EventBus.$emit("gameLoaded", rom);
                        resolve({ rom: this.rom, patch: response.data.patch });
                      }.bind(this)
                    );
                  })
                  .catch(error => {
                    this.error = this.$i18n.t("error.failed_generation");
                    this.generating = false;
                    reject(error);
                  });
              } else {
                this.error = this.$i18n.t("error.failed_generation");
                this.generating = false;
              }
            })
            .catch(error => {
              if (error.response) {
                switch (error.response.status) {
                  case 429:
                    this.error = this.$i18n.t("error.429");
                    break;
                  default:
                    this.error = this.$i18n.t("error.failed_generation");
                }
              }

              this.generating = false;
              reject(error);
            });
        }.bind(this)
      );
    },
    async checkGeneration() {
      const response =
        await axios.get(
          `/api/generation/seed/${this.generationId}`,
          { responseType: "json" });

      if (response.data.status === "waiting") {
        throw new Error("not ready yet");
      } else if (response.data.seed_hash) {
        return await axios.get(`/hash/${response.data.seed_hash}`);
      } else {
        throw new Error("generation failed");
      }
    },
    saveRom() {
      // track the sprite choice for usage statistics
      localforage.getItem("rom.sprite-gfx").then(value => {
        ga("event", "save", {
          dimension1: value
        });
      });
      return this.rom.save(this.rom.downloadFilename() + ".sfc", {
        quickswap: this.quickswap,
        paletteShuffle: this.paletteShuffle,
        musicOn: this.musicOn,
        msu1Resume: this.msu1Resume,
        reduceFlashing: this.reduceFlashing,
        shuffleSfx: this.shuffleSfx,
        fakeBoots: this.fakeBoots,
        icePhysics: this.icePhysics,
      });
    },
    saveSpoiler() {
      return FileSaver.saveAs(
        new Blob([JSON.stringify(this.rom.spoiler, null, 4)]),
        this.rom.downloadFilename() + ".txt"
      );
    },
    updateRom(rom_infos) {
      this.rom_infos = rom_infos;
      this.error = false;
      this.romLoaded = true;
    },
    onError(error) {
      this.error = error;
    }
  },
  computed: {
    ...mapState("randomizer", {
      optionsPreset: state => state.options.preset,
      preset: state => state.preset,
      optionsGlitchesRequired: state => state.options.glitches_required,
      glitchesRequired: state => state.glitches_required,
      optionsItemPlacement: state => state.options.item_placement,
      itemPlacement: state => state.item_placement,
      optionsDungeonItems: state => state.options.dungeon_items,
      dungeonItems: state => state.dungeon_items,
      optionsBossItems: state => state.options.boss_items,
      bossItems: state => state.boss_items,
      optionsDropShuffle: state => state.options.drop_shuffle,
      dropShuffle: state => state.drop_shuffle,
      optionsBonkShuffle: state => state.options.bonk_shuffle,
      bonkShuffle: state => state.bonk_shuffle,
      optionsPotteryShuffle: state => state.options.pottery_shuffle,
      potteryShuffle: state => state.pottery_shuffle,
      optionsAccessibility: state => state.options.accessibility,
      accessibility: state => state.accessibility,
      optionsGoal: state => state.options.goal,
      goal: state => state.goal,
      optionsTowerOpen: state => state.options.tower_open,
      towerOpen: state => state.tower_open,
      optionsGanonOpen: state => state.options.ganon_open,
      ganonOpen: state => state.ganon_open,
      optionsGanonItem: state => state.options.ganon_item,
      ganonItem: state => state.ganon_item,
      optionsWorldState: state => state.options.world_state,
      worldState: state => state.world_state,
      optionsEntranceShuffle: state => state.options.entrance_shuffle,
      entranceShuffle: state => state.entrance_shuffle,
      optionsDoorShuffle: state => state.options.door_shuffle,
      doorShuffle: state => state.door_shuffle,
      optionsDoorIntensity: state => state.options.door_intensity,
      doorIntensity: state => state.door_intensity,
      optionsDoorTypeMode: state => state.options.door_type_mode,
      doorTypeMode: state => state.door_type_mode,
      optionsTrapDoorMode: state => state.options.trap_door_mode,
      trapDoorMode: state => state.trap_door_mode,
      optionsDecoupleDoors: state => state.options.decouple_doors,
      decoupleDoors: state => state.decouple_doors,
      optionsOverworldShuffle: state => state.options.ow_shuffle,
      overworldShuffle: state => state.ow_shuffle,
      optionsOverworldCrossed: state => state.options.ow_crossed,
      overworldCrossed: state => state.ow_crossed,
      optionsOverworldKeepSimilar: state => state.options.ow_keep_similar,
      overworldKeepSimilar: state => state.ow_keep_similar,
      optionsOverworldMixed: state => state.options.ow_mixed,
      overworldMixed: state => state.ow_mixed,
      optionsOverworldFluteShuffle: state => state.options.ow_flute_shuffle,
      overworldFluteShuffle: state => state.ow_flute_shuffle,
      optionsOverworldWhirlpoolShuffle: state => state.options.ow_whirlpool_shuffle,
      overworldWhirlpoolShuffle: state => state.ow_whirlpool_shuffle,
      optionsShopsanity: state => state.options.shopsanity,
      shopsanity: state => state.shopsanity,
      optionsBossShuffle: state => state.options.boss_shuffle,
      bossShuffle: state => state.boss_shuffle,
      optionsEnemyShuffle: state => state.options.enemy_shuffle,
      enemyShuffle: state => state.enemy_shuffle,
      optionsHints: state => state.options.hints,
      hints: state => state.hints,
      optionsWeapons: state => state.options.weapons,
      weapons: state => state.weapons,
      optionsItemPool: state => state.options.item_pool,
      itemPool: state => state.item_pool,
      optionsItemFunctionality: state => state.options.item_functionality,
      itemFunctionality: state => state.item_functionality,
      optionsStartingFlute: state => state.options.starting_flute,
      startingFlute: state => state.starting_flute,
      optionsStartingBoots: state => state.options.starting_boots,
      startingBoots: state => state.starting_boots,
      optionsEnemyDamage: state => state.options.enemy_damage,
      enemyDamage: state => state.enemy_damage,
      optionsEnemyHealth: state => state.options.enemy_health,
      enemyHealth: state => state.enemy_health
    }),
    ...mapState("romSettings", {
      heartSpeed: state => state.heartSpeed,
      menuSpeed: state => state.menuSpeed,
      heartColor: state => state.heartColor,
      quickswap: state => state.quickswap,
      musicOn: state => state.musicOn,
      msu1Resume: state => state.msu1Resume,
      paletteShuffle: state => state.paletteShuffle,
      reduceFlashing: state => state.reduceFlashing,
      shuffleSfx: state => state.shuffleSfx,
      fakeBoots: state => state.fakeBoots,
      icePhysics: state => state.icePhysics,
    }),
    editable() {
      return this.$store.state.randomizer.preset.value === "custom";
    }
  }
};
</script>
